import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  Card,
  Divider,
  Input,
  IconButton,
  Paper,
  Select,
} from "@mui/material";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

// Child component for rendering a single event detail item
const EventDetailItem = ({
  index,
  control,
  setValue,
  remove,
  detail,
  handleDetailImageUpload,
}) => {
  // Now we can safely call useWatch at the top of this component
  const detailType = useWatch({
    control,
    name: `eventDetails[${index}].type`,
    defaultValue: detail.type,
  });

  return (
    <Grid item xs={12}>
      <Grid container spacing={2}>
        {/* Detail Type Selector */}
        <Grid item xs={3}>
          <Controller
            name={`eventDetails[${index}].type`}
            control={control}
            render={({ field: { onChange, value, ...restField } }) => (
              <Select
                {...restField}
                value={value}
                fullWidth
                variant="outlined"
                onChange={(e) => {
                  const newType = e.target.value;
                  onChange(e); // update RHF state
                  if (newType === "image") {
                    setValue(`eventDetails[${index}].content`, "", {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }
                }}
              >
                <MenuItem value="description">Description</MenuItem>
                <MenuItem value="image">Image</MenuItem>
              </Select>
            )}
          />
        </Grid>

        {/* Conditional Input based on detail type */}
        <Grid item xs={7}>
          {detailType === "image" ? (
            <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handleDetailImageUpload(index, e)}
                sx={{ display: "none" }}
                id={`detail-image-upload-${index}`}
              />
              <label htmlFor={`detail-image-upload-${index}`}>
                <IconButton color="primary" component="span">
                  <AddPhotoAlternateIcon fontSize="large" />
                </IconButton>
              </label>
              <Controller
                name={`eventDetails[${index}].content`}
                control={control}
                render={({ field }) =>
                  field.value && (
                    <img
                      src={field.value}
                      alt="Detail"
                      height="120"
                      style={{ marginTop: 8 }}
                    />
                  )
                }
              />
            </Paper>
          ) : (
            <Controller
              name={`eventDetails[${index}].content`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Enter description"
                  multiline
                  fullWidth
                  minRows={4}
                  variant="outlined"
                />
              )}
            />
          )}
        </Grid>

        {/* Remove Button */}
        <Grid item xs={2}>
          <IconButton color="error" onClick={() => remove(index)}>
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

const AddEvent = () => {
  const { register, handleSubmit, control, reset, setValue, watch } = useForm({
    defaultValues: {
      name: "",
      image: "",
      start_date: "",
      end_date: "",
      eventDetails: [{ type: "description", content: "" }],
    },
  });
  const [eventImage, setEventImage] = useState(null);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "eventDetails",
  });

  const onSubmit = (data) => {
    console.log("Form Data:", data);
    console.log("Event Image:", eventImage);
  };

  const handleEventImageUpload = (e) => {
    if (e.target.files.length > 0) {
      setEventImage(URL.createObjectURL(e.target.files[0]));
      setValue("image", e.target.files[0]);
    }
  };

  const handleDetailChange = (index, field, value) => {
    setValue(`eventDetails[${index}].${field}`, value, {
      shouldValidate: true,
    });
  };

  const handleDetailImageUpload = (index, e) => {
    if (e.target.files.length > 0) {
      const imageUrl = URL.createObjectURL(e.target.files[0]);
      handleDetailChange(index, "content", imageUrl);
    }
  };

  return (
    <Container sx={{ padding: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Add New Event
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4} direction="column">
          {/* Event Information Section */}
          <Grid item xs={12}>
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Event Information
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Event Name"
                    {...register("name")}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    {...register("start_date")}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    {...register("end_date")}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                    Event Image
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleEventImageUpload}
                      sx={{ display: "none" }}
                      id="event-image-upload"
                    />
                    <label htmlFor="event-image-upload">
                      <IconButton color="primary" component="span">
                        <AddPhotoAlternateIcon fontSize="large" />
                      </IconButton>
                    </label>
                    {eventImage && (
                      <img
                        src={eventImage}
                        alt="Event"
                        height="120"
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Event Details Section */}
          <Grid item xs={12}>
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Event Details
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                {fields.map((detail, index) => (
                  <Grid item xs={12} key={detail.id}>
                    <Grid container spacing={2}>
                      {/* Detail Type Selection */}
                      <Grid item xs={3}>
                        <Controller
                          name={`eventDetails[${index}].type`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              fullWidth
                              variant="outlined"
                              onChange={(e) => {
                                const newType = e.target.value;
                                setValue(
                                  `eventDetails[${index}].type`,
                                  newType,
                                  { shouldValidate: true } // ensures immediate UI updates and validation in Reach Hook Form
                                );

                                // Clear previous content when switching type
                                if (newType === "image") {
                                  setValue(
                                    `eventDetails[${index}].content`,
                                    "",
                                    { shouldValidate: true }
                                  );
                                } else if (newType === "description") {
                                  setValue(
                                    `eventDetails[${index}].content`,
                                    "",
                                    { shouldValidate: true }
                                  );
                                }
                              }}
                            >
                              <MenuItem value="description">
                                Description
                              </MenuItem>
                              <MenuItem value="image">Image</MenuItem>
                            </Select>
                          )}
                        />
                      </Grid>

                      {/* Conditional Rendering for Input Types */}
                      <Grid item xs={7}>
                        {watch(`eventDetails[${index}].type`) === "image" ? ( // use watch to track the value dynamically
                          <Paper
                            variant="outlined"
                            sx={{ p: 2, textAlign: "center" }}
                          >
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleDetailImageUpload(index, e)
                              }
                              sx={{ display: "none" }}
                              id={`detail-image-upload-${index}`}
                            />
                            <label htmlFor={`detail-image-upload-${index}`}>
                              <IconButton color="primary" component="span">
                                <AddPhotoAlternateIcon fontSize="large" />
                              </IconButton>
                            </label>
                            {watch(`eventDetails[${index}].content`) && (
                              <img
                                src={watch(`eventDetails[${index}].content`)}
                                alt="Detail Image"
                                height="120"
                                style={{ marginTop: 8 }}
                              />
                            )}
                          </Paper>
                        ) : (
                          <Controller
                            name={`eventDetails[${index}].content`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                placeholder="Enter description"
                                multiline
                                fullWidth
                                minRows={4}
                                variant="outlined"
                              />
                            )}
                          />
                        )}
                      </Grid>

                      {/* Delete Button */}
                      <Grid item xs={2}>
                        <IconButton color="error" onClick={() => remove(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              {/* Add More Details */}
              <Button
                onClick={() => append({ type: "description", content: "" })}
                startIcon={<AddIcon />}
                variant="outlined"
              >
                Add More
              </Button>
            </Card>
          </Grid>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<SaveIcon />}
              onClick={() => reset()}
            >
              Save Draft
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
            >
              Add Event
            </Button>
          </Stack>
        </Grid>
      </form>
    </Container>
  );
};

export default AddEvent;
